'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchAdSpendVsSales } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';

function fmt(val: number) {
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  return `₹${(val / 1000).toFixed(0)}K`;
}

interface TTProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TTProps) {
  if (!active || !payload) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-dropdown px-3 py-2 text-xs">
      <p className="font-600 text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={`ads-tt-${i}`} style={{ color: p.color }}>
          {p.name === 'adSpend' ? 'Ad Spend' : 'Ad Sales'}: <span className="font-600">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

export default function AdSpendVsSalesChart() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    fetchAdSpendVsSales(30).then((result) => {
      if (result) setData(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();

    const supabase = createClient();
    const channel = supabase
      .channel('adspend-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ad_campaigns' }, loadData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  if (loading) return <div className="h-[220px] bg-muted/30 rounded-lg animate-pulse" />;
  if (!data.length) return <div className="h-[220px] flex items-center justify-center text-xs text-muted-foreground">No ad data available</div>;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="adSpendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--warning)" stopOpacity={0.15} />
            <stop offset="95%" stopColor="var(--warning)" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="adSalesGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--positive)" stopOpacity={0.18} />
            <stop offset="95%" stopColor="var(--positive)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} interval={4} />
        <YAxis tickFormatter={fmt} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} width={52} />
        <Tooltip content={<CustomTooltip />} />
        <Legend formatter={(val) => <span className="text-xs text-muted-foreground">{val === 'adSpend' ? 'Ad Spend' : 'Attributed Sales'}</span>} iconType="circle" iconSize={7} />
        <Area type="monotone" dataKey="adSpend" stroke="var(--warning)" strokeWidth={1.5} fill="url(#adSpendGrad)" dot={false} />
        <Area type="monotone" dataKey="adSales" stroke="var(--positive)" strokeWidth={2} fill="url(#adSalesGrad)" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}